import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../contexts/AuthContext'
import { signInWithGoogle } from '../services/oauth'
import './AuthModal.css'

function AuthModal({ isOpen, onClose }) {
  const { checkEmail, signInWithEmail, signUpWithEmail, authReturnPath, setGuestMode } = useAuth()
  const navigate = useNavigate()
  
  // 步骤状态
  const [step, setStep] = useState(1) // 1: 邮箱输入, 2: 完整注册表单
  
  // 表单数据
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [newsletter, setNewsletter] = useState(false)
  
  // UI状态
  const [isLogin, setIsLogin] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // 重置表单
  const resetForm = () => {
    setStep(1)
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setFirstName('')
    setLastName('')
    setPhoneNumber('')
    setNewsletter(false)
    setError('')
    setShowPassword(false)
    setShowConfirmPassword(false)
  }

  // 关闭模态框
  const handleClose = () => {
    resetForm()
    onClose()
  }

  // 第一步：邮箱验证
  const handleEmailContinue = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const result = await checkEmail(email)
      
      if (result.success) {
        if (result.exists) {
          // 如果邮箱已存在，切换到登录模式
          setIsLogin(true)
          setStep(2)
        } else {
          // 如果邮箱不存在，进入注册流程
          console.log('Email does not exist, switching to signup mode')
          setIsLogin(false)
          setStep(2)
        }
      } else {
        console.error('Email check failed:', result.error)
        setError(result.error || 'Failed to verify email address')
      }
    } catch (error) {
      console.error('Email check error:', error)
      setError('Failed to verify email address')
    } finally {
      setLoading(false)
    }
  }

  // 第二步：登录/注册
  const handleAuthSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      let result
      if (isLogin) {
        result = await signInWithEmail(email, password)
      } else {
        // 验证密码匹配
        if (password !== confirmPassword) {
          setError('Passwords do not match')
          return
        }
        
        const fullName = `${firstName} ${lastName}`.trim()
        result = await signUpWithEmail(email, password, fullName)
      }

      if (result.success) {
        handleClose()
        // 登录/注册成功后跳转到指定路径
        navigate(authReturnPath || '/')
      } else {
        setError(result.error || 'Authentication failed')
      }
    } catch (error) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  // Google登录
  const handleGoogleAuth = async () => {
    setLoading(true)
    setError('')

    try {
      // 保存返回路径到 localStorage，OAuth callback 时会使用
      localStorage.setItem('oauth_return_path', authReturnPath || '/')
      console.log('jianan authReturnPath', authReturnPath)
      const result = await signInWithGoogle()
      
      if (result.success) {
        // OAuth重定向会处理登录，这里不需要额外处理
        // 用户会被重定向到Google，然后回到我们的网站
      }
    } catch (error) {
      setError('Google authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const handleGuestMode = () => {
    // Guest mode 不需要验证 email，直接进入 checkout
    // Email 会在 checkout 页面填写
    setGuestMode(true) // 设置 guest mode 为 true
    if (authReturnPath == '/cart') {
      navigate('/checkout')
    }
    else {
      navigate('/')
    }
  }

  if (!isOpen) return null

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={handleClose}>
          <svg viewBox="0 0 24 24">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {step === 1 ? (
          // 第一步：邮箱输入
          <div className="auth-step-1">
            <h2 className="auth-title">LOGIN / CREATE ACCOUNT</h2>
            <p className="auth-description">
              Please enter your email address, and we'll check if you already have an account. You may also continue as a Guest.
            </p>
            
            <form onSubmit={handleEmailContinue} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email Address*</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address*"
                  required
                />
                <div className="required-fields">*Required Fields</div>
              </div>
              
              <button type="submit" className="continue-btn" disabled={loading}>
                {loading ? 'CHECKING...' : 'CONTINUE'}
              </button>
            </form>

            <div className="auth-divider">
              <span>OR</span>
            </div>

            <div className="social-login">
              <button className="google-btn" onClick={handleGuestMode} disabled={loading}>
                CONTINUE AS GUEST
              </button>
              <button className="google-btn" onClick={handleGoogleAuth} disabled={loading} style={{ marginTop: '12px' }}>
                <svg className="google-icon" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                CONTINUE WITH GOOGLE
              </button>
            </div>

            <div className="auth-footer">
              <div className="customer-service">
                <h4>Customer Service</h4>
                <p>Country: <span className="underline">United States</span></p>
              </div>
            </div>
          </div>
        ) : (
          // 第二步：登录/注册表单
          <div className="auth-step-2">
            <h2 className="auth-title">{isLogin ? 'LOGIN' : 'CREATING ACCOUNT'}</h2>
            <p className="auth-description">
              {isLogin 
                ? 'Please enter your password to sign in to your account.'
                : 'Please fill in the fields below to create your account and get access to exclusive services.'
              }
            </p>
            <div className="required-fields">*Required Fields</div>
            
            <form onSubmit={handleAuthSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email-step2">Email Address*</label>
                <input
                  type="email"
                  id="email-step2"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address*"
                  required
                  disabled
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password*</label>
                <div className="password-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password*"
                    required
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <svg viewBox="0 0 24 24">
                      {showPassword ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              {!isLogin && (
                <>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password*</label>
                    <div className="password-group">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm Password*"
                        required
                      />
                      <button 
                        type="button" 
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        <svg viewBox="0 0 24 24">
                          {showConfirmPassword ? (
                            <>
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                              <line x1="1" y1="1" x2="23" y2="23"/>
                            </>
                          ) : (
                            <>
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name*</label>
                      <input
                        type="text"
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First Name*"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name*</label>
                      <input
                        type="text"
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last Name*"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phoneNumber">Phone Number</label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Phone Number"
                    />
                  </div>

                  <div className="legal-section">
                    <p className="terms-text">
                      By continuing, you agree to p1ur's <a href="#" className="terms-link">Terms of Use</a>.
                    </p>
                    <label className="newsletter-checkbox">
                      <input
                        type="checkbox"
                        checked={newsletter}
                        onChange={(e) => setNewsletter(e.target.checked)}
                      />
                      <span>I would like to receive p1ur newsletter. (Optional)</span>
                    </label>
                  </div>
                </>
              )}

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="signup-btn" disabled={loading}>
                {loading ? (isLogin ? 'SIGNING IN...' : 'CREATING ACCOUNT...') : (isLogin ? 'SIGN IN' : 'SIGN UP')}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}

export default AuthModal